import { useQuery, useMutation } from 'react-query'
import { isEmpty } from 'lodash-es'
import uuid from 'uuid'
import { useCookies } from 'react-cookie'
import { useAppDispatch } from '@/redux/hooks'

// Libs
import { fetchPchomeToken } from '@/api/fetchPchomeToken'
import { fetchMemberData } from '@/api/fetchMemberData'
import { setMemberData } from '@/redux/reducers/member'
import { updateMemberData } from '@/api/updateMemberData'

// Constants
import { QUERY_KEY } from '@/constants/queryKey'
import { COOKIE_DATA, COOKIE_OPTIONS } from '@/constants/cookieKey'
import { STORAGE_DATA } from '@/constants/storageData'
import { useEffect, useState } from 'react'

interface IProps {
  userName: string | null
  setUsername: (payload: any) => void
  closeSettingUsernamePopup: () => void
  alwaysGuest?: boolean
  brandName: string
}

export const usePCHomeUUID = ({ userName, setUsername, closeSettingUsernamePopup, alwaysGuest, brandName }: IProps) => {
  const dispatch = useAppDispatch()
  const [cookies, setCookie] = useCookies([COOKIE_DATA.PCHOME_UUID, COOKIE_DATA.PCHOME_NAME_EXPIRED])
  const cookieUuid = !isEmpty(cookies[COOKIE_DATA.PCHOME_UUID]) ? cookies[COOKIE_DATA.PCHOME_UUID] : null
  let uuidState = false
  let internalUUID = null
  const pchomeLocalStorageToken = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_DATA.TOKEN)
  const [isErrorFetchPchomeToken, setIsErrorFetchPchomeToken] = useState(false)
  const [fetchPchomeTokenErrorData, setFetchPchomeTokenErrorData] = useState(null)
  const [isErrorUpdateNickname, setIsErrorUpdateNickname] = useState(false)
  const [updateNicknameErrorData, setUpdateNicknameErrorData] = useState(null)
  const [shouldUpdateMemberData, setShouldUpdateMemberData] = useState(false)
  const [pendingNickname, setPendingNickname] = useState<string | null>(null)

  if (!isEmpty(cookieUuid)) {
    uuidState = true
  } else if (typeof window !== 'undefined' && isEmpty(cookieUuid) && !isEmpty(userName)) {
    internalUUID = uuid.v4()
    // TODO: cookie option 還要再調整
    setCookie(COOKIE_DATA.PCHOME_UUID, internalUUID, { path: '/', maxAge: 18316800, domain: COOKIE_OPTIONS.PCHOME.DOMAIN })
  }

  const {
    data,
    isSuccess,
    isLoading: isLoadingFetchPChomeToken,
  } = useQuery(
    [QUERY_KEY.FETCH_PCHOME_TOKEN, { internalUUID, userName }],
    // thridPlatform 拼字是後端定的，不改
    () => fetchPchomeToken({ uid: cookieUuid, name: userName, thridPlatform: brandName }),
    {
      // alwaysGuest 模式: 不管有沒有 token 都要執行 fetchPchomeToken
      // 一般模式: 只有在沒有 token 的情況下才執行
      enabled:
        (alwaysGuest && !isEmpty(cookieUuid) && !isEmpty(userName)) ||
        (!isEmpty(cookieUuid) && !isEmpty(userName) && isEmpty(pchomeLocalStorageToken)),
      onSuccess: (successData) => {
        const { token, refreshToken } = successData
        const tokenData = { token, refreshToken }

        window.localStorage.setItem(STORAGE_DATA.TOKEN, JSON.stringify(tokenData))
        setCookie(COOKIE_DATA.PCHOME_NAME_EXPIRED, 1, {
          path: '/',
          domain: COOKIE_OPTIONS.PCHOME.DOMAIN,
          expires: new Date(new Date().getTime() + 2 * 60 * 60 * 1000),
        })

        // alwaysGuest 模式: 保存 nickname 並標記需要更新 memberData
        if (alwaysGuest && userName) {
          setPendingNickname(userName)
          setShouldUpdateMemberData(true)
        } else {
          closeSettingUsernamePopup()
        }
      },
      onError: (data: any) => {
        setIsErrorFetchPchomeToken(true)
        setFetchPchomeTokenErrorData(data)
      },
      onSettled: () => {
        setUsername(null)
      },
    },
  )

  const pchomeToken = data?.token

  const { refetch: refetchMemberData } = useQuery([QUERY_KEY.FETCH_MEMBER_DATA], () => fetchMemberData(), {
    // 一般模式才會自動觸發 fetchMemberData，alwaysGuest 模式會在 updateMemberData 成功後手動觸發
    enabled: !alwaysGuest && isSuccess && !isEmpty(cookieUuid) && !isEmpty(pchomeToken),
    onSuccess: (data) => {
      dispatch(setMemberData({ memberData: { ...data } }))
      window.dispatchEvent(new Event('localStorageChanged'))
    },
    onSettled: () => {
      dispatch(setMemberData({ memberData: { isFetched: true } }))
    },
  })

  const updateMemberDataMutation = useMutation(updateMemberData, {
    onSuccess: async () => {
      // alwaysGuest 模式: updateMemberData 成功後才關閉 popup
      if (alwaysGuest) {
        await refetchMemberData()
        closeSettingUsernamePopup()
      }
    },
    onError: (data: any) => {
      setIsErrorUpdateNickname(true)
      setUpdateNicknameErrorData(data)
    },
  })

  const updateNickname = async () => {
    try {
      await updateMemberDataMutation.mutateAsync({ nickName: userName })
      // 一般模式才需要 refetch 和設定 cookie
      if (!alwaysGuest) {
        await refetchMemberData()
        setCookie(COOKIE_DATA.PCHOME_NAME_EXPIRED, 1, {
          path: '/',
          domain: COOKIE_OPTIONS.PCHOME.DOMAIN,
          expires: new Date(new Date().getTime() + 2 * 60 * 60 * 1000),
        })
      }
    } catch (e) {
      console.log('update nickname error')
    }
  }

  // alwaysGuest 模式: fetchPchomeToken 成功後，用新 token 去 updateMemberData
  useEffect(() => {
    if (alwaysGuest && shouldUpdateMemberData && !isEmpty(pendingNickname)) {
      updateMemberDataMutation
        .mutateAsync({ nickName: pendingNickname })
        .then(() => {
          setShouldUpdateMemberData(false)
          setPendingNickname(null)
        })
        .catch((e) => {
          console.log('update nickname error for alwaysGuest mode:', e)
        })
    }
  }, [alwaysGuest, shouldUpdateMemberData, pendingNickname])

  // 一般模式: 如果已有 token，直接 updateNickname
  if (!alwaysGuest && !isEmpty(cookieUuid) && !isEmpty(userName) && !isEmpty(pchomeLocalStorageToken)) {
    updateNickname()
    setUsername(null)
  }

  useEffect(() => {
    if (!alwaysGuest && updateMemberDataMutation.isSuccess) {
      closeSettingUsernamePopup()
    }
  }, [alwaysGuest, updateMemberDataMutation.isSuccess])

  return {
    hasPChomeUUID: uuidState,
    isLoadingFetchPChomeToken,
    isLoadingUpdateNickname: updateMemberDataMutation.isLoading,
    isErrorFetchPchomeToken,
    fetchPchomeTokenErrorData,
    setIsErrorFetchPchomeToken,
    isErrorUpdateNickname,
    updateNicknameErrorData,
    setIsErrorUpdateNickname,
  }
}
